import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { SystemBackup } from '../components/SystemBackup';
import { AnalyticsDashboard } from '../components/AnalyticsDashboard';
import { useSettings } from '../hooks/useSettings';
import { Header } from '../components/Header';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';

interface Tenant {
  id: string;
  slug: string;
  name: string;
  domain?: string;
  active: boolean;
  expiresAt?: string | null; // Data de expiração do tenant
  allowedProviders?: string[]; // ['WAHA', 'EVOLUTION', 'QUEPASA']
  timezone?: string; // Fuso horário do tenant
  createdAt: string;
  updatedAt: string;
  users?: Array<{
    id: string;
    nome: string;
    email: string;
    role: string;
    ativo: boolean;
  }>;
  _count?: {
    users: number;
    contacts: number;
    campaigns: number;
    whatsappSessions: number;
  };
  quota?: {
    maxUsers: number;
    maxContacts: number;
    maxCampaigns: number;
    maxConnections: number;
    maxMessagesPerMonth: number;
    messagesSentThisMonth: number;
    billingCycleDay: number;
  };
}

interface User {
  id: string;
  nome: string;
  email: string;
  role: string;
  ativo: boolean;
  ultimoLogin?: string | null;
  criadoEm: string;
  atualizadoEm: string;
  tenant?: {
    id: string;
    name: string;
    slug: string;
    active: boolean;
  } | null;
}

interface TenantFormData {
  name: string;
  expiresAt: string; // Data de expiração (vazio = sem expiração)
  timezone: string; // Fuso horário
  adminUser: {
    nome: string;
    email: string;
    senha: string;
  };
  quotas: {
    maxUsers: string;
    maxContacts: string;
    maxCampaigns: string;
    maxConnections: string;
    maxMessagesPerMonth: string;
    billingCycleDay: string;
  };
  allowedProviders: string[]; // ['WAHA', 'EVOLUTION', 'QUEPASA']
}

interface SystemStats {
  tenants: {
    total: number;
    active: number;
    inactive: number;
    expiringToday: number;
    expiringIn7Days: number;
  };
  users: {
    total: number;
    byRole: {
      SUPERADMIN: number;
      ADMIN: number;
      USER: number;
    };
  };
  resources: {
    totalContacts: number;
    totalCampaigns: number;
    totalSessions: number;
    totalMessages: number;
    totalBots: number;
    totalBotResponses: number;
  };
  activity: {
    activeToday: number;
    activeCampaigns: number;
    workingSessions: number;
  };
}

interface BackupInfo {
  tenantId: string;
  tenantSlug: string;
  size: number;
  createdAt: string;
  status: 'success' | 'failed' | 'in_progress';
  error?: string;
}

interface BackupStats {
  totalTenants: number;
  totalBackups: number;
  totalSize: number;
  scheduledJobs: number;
  tenantStats: {
    tenantId: string;
    tenantSlug: string;
    tenantName: string;
    backupCount: number;
    totalSize: number;
    lastBackup: string | null;
    isScheduled: boolean;
  }[];
}


interface Settings {
  id: string;
  wahaHost: string;
  wahaApiKey: string;
  evolutionHost: string;
  evolutionApiKey: string;
  quepasaUrl?: string;
  quepasaLogin?: string;
  quepasaPassword?: string;
  logoUrl?: string;
  companyName?: string;
  faviconUrl?: string;
  pageTitle?: string;
  iconUrl?: string;
  openaiApiKey?: string;
  groqApiKey?: string;
}

const settingsSchema = z.object({
  wahaHost: z.string().refine((val) => !val || z.string().url().safeParse(val).success, {
    message: 'Host deve ser uma URL válida ou vazio'
  }),
  wahaApiKey: z.string().refine((val) => !val || val.length >= 10, {
    message: 'API Key deve ter pelo menos 10 caracteres ou estar vazia'
  }),
  evolutionHost: z.string().refine((val) => !val || z.string().url().safeParse(val).success, {
    message: 'Host deve ser uma URL válida ou vazio'
  }),
  evolutionApiKey: z.string().refine((val) => !val || val.length >= 10, {
    message: 'API Key deve ter pelo menos 10 caracteres ou estar vazia'
  }),
  quepasaUrl: z.string().refine((val) => !val || z.string().url().safeParse(val).success, {
    message: 'URL deve ser uma URL válida ou vazio'
  }),
  quepasaLogin: z.string().optional(),
  quepasaPassword: z.string().optional(),
});

const generalSettingsSchema = z.object({
  companyName: z.string().refine((val) => !val || (val.length >= 1 && val.length <= 100), {
    message: 'Nome da empresa deve ter entre 1 e 100 caracteres ou estar vazio'
  }),
  pageTitle: z.string().refine((val) => !val || (val.length >= 1 && val.length <= 100), {
    message: 'Título da página deve ter entre 1 e 100 caracteres ou estar vazio'
  }),
});

type SettingsFormData = z.infer<typeof settingsSchema>;
type GeneralSettingsFormData = z.infer<typeof generalSettingsSchema>;

type TabKey = 'general' | 'integrations' | 'tenants' | 'users' | 'backup';

const PATH_TO_TAB: Record<string, TabKey> = {
  '/superadmin/geral': 'general',
  '/superadmin/integracoes': 'integrations',
  '/superadmin/empresas': 'tenants',
  '/superadmin/usuarios': 'users',
  '/superadmin/backup': 'backup',
};

export function SuperAdminManagerPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>(() => {
    const fromPath = PATH_TO_TAB[location.pathname];
    if (fromPath) return fromPath;
    // fallback antigo: estado vindo do dashboard
    const fromState = (location.state as any)?.tab as TabKey | undefined;
    return fromState && ['general', 'integrations', 'tenants', 'users', 'backup'].includes(fromState)
      ? fromState
      : 'general';
  });

  // Mantém a aba ativa sincronizada com a rota atual do superadmin
  useEffect(() => {
    const fromPath = PATH_TO_TAB[location.pathname];
    if (fromPath && fromPath !== activeTab) {
      setActiveTab(fromPath);
    }
  }, [location.pathname, activeTab]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [backupStats, setBackupStats] = useState<BackupStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [backupLoading, setBackupLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [formData, setFormData] = useState<TenantFormData>({
    name: '',
    expiresAt: '',
    timezone: 'America/Sao_Paulo',
    adminUser: {
      nome: '',
      email: '',
      senha: ''
    },
    quotas: {
      maxUsers: '10',
      maxContacts: '0',
      maxCampaigns: '0',
      maxConnections: '5',
      maxMessagesPerMonth: '0',
      billingCycleDay: '1'
    },
    allowedProviders: ['WAHA', 'EVOLUTION', 'QUEPASA']
  });
  const [tenantSearchQuery, setTenantSearchQuery] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [userTotalPages, setUserTotalPages] = useState(1);
  const [userTotal, setUserTotal] = useState(0);
  const [openActionsId, setOpenActionsId] = useState<string | null>(null);
  const [dropdownPlacement, setDropdownPlacement] = useState<{ left: number; top: number; bottom?: number; openUp?: boolean } | null>(null);
  const [openUserActionsId, setOpenUserActionsId] = useState<string | null>(null);
  const [userDropdownPlacement, setUserDropdownPlacement] = useState<{ left: number; top: number; bottom?: number; openUp?: boolean } | null>(null);

  const actionsDropdownRef = useRef<HTMLDivElement>(null);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const userActionsDropdownRef = useRef<HTMLDivElement>(null);
  const userActionsMenuRef = useRef<HTMLDivElement>(null);

  const [activeModal, setActiveModal] = useState<'waha' | 'evolution' | 'quepasa' | null>(null);
  const [integrationSettings, setIntegrationSettings] = useState<Settings | null>(null);

  // General settings states
  const [generalSettings, setGeneralSettings] = useState<Settings | null>(null);

  // Logo states
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Favicon states
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [faviconPreview, setFaviconPreview] = useState<string>('');
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  // Icon states
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string>('');
  const [uploadingIcon, setUploadingIcon] = useState(false);

  // Fechar dropdown ao clicar fora (empresas)
  useEffect(() => {
    if (!openActionsId) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target as Node) &&
          actionsDropdownRef.current && !actionsDropdownRef.current.contains(e.target as Node)) {
        setOpenActionsId(null);
        setDropdownPlacement(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionsId]);

  // Fechar dropdown ao clicar fora (usuários)
  useEffect(() => {
    if (!openUserActionsId) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (userActionsMenuRef.current && !userActionsMenuRef.current.contains(e.target as Node) &&
          userActionsDropdownRef.current && !userActionsDropdownRef.current.contains(e.target as Node)) {
        setOpenUserActionsId(null);
        setUserDropdownPlacement(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openUserActionsId]);

  useEffect(() => {
    loadData();
    if (activeTab === 'integrations') {
      loadIntegrationSettings();
    }
    if (activeTab === 'general') {
      loadGeneralSettings();
    }

    // Listener para atualização de quota (quando campanha é criada/iniciada)
    const handleQuotaUpdate = () => loadData();
    window.addEventListener('quota-updated', handleQuotaUpdate);

    // Ao voltar para a aba do navegador: não recarrega Empresas nem Usuários (listas pesadas)
    const handleFocus = () => {
      if (activeTab === 'tenants' || activeTab === 'users') return;
      loadData();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('quota-updated', handleQuotaUpdate);
      window.removeEventListener('focus', handleFocus);
    };
  }, [activeTab]);

  const {
    register,
    handleSubmit: handleFormSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<SettingsFormData>({
    resolver: zodResolver(settingsSchema),
  });

  // General settings form
  const {
    register: registerGeneral,
    handleSubmit: handleGeneralSubmit,
    setValue: setGeneralValue,
    formState: { errors: generalErrors, isSubmitting: isGeneralSubmitting },
  } = useForm<GeneralSettingsFormData>({
    resolver: zodResolver(generalSettingsSchema),
  });

  // Helper para fazer requisições autenticadas
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  // Helper para uploads (sem Content-Type automatico)
  const authenticatedUpload = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  const loadGeneralSettings = async () => {
    try {
      const response = await authenticatedFetch('/api/settings');
      if (response.ok) {
        const data = await response.json();
        setGeneralSettings(data);
        setGeneralValue('companyName', data.companyName || '');
        setGeneralValue('pageTitle', data.pageTitle || '');
      }
    } catch (error) {
      console.error('Erro ao carregar configurações gerais:', error);
      toast.error('Erro ao carregar configurações gerais');
    }
  };

  const onGeneralSubmit = async (data: GeneralSettingsFormData) => {
    try {
      const response = await authenticatedFetch('/api/settings', {
        method: 'PUT',
        body: JSON.stringify(data),
      });

      if (response.ok) {
        toast.success('Configurações gerais salvas com sucesso');
        loadGeneralSettings();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao salvar configurações gerais');
      }
    } catch (error) {
      console.error('Erro ao salvar configurações gerais:', error);
      toast.error('Erro ao salvar configurações gerais');
    }
  };

  const loadIntegrationSettings = async () => {
    try {
      const response = await authenticatedFetch('/api/settings');
      if (response.ok) {
        const data = await response.json();
        setIntegrationSettings(data);
        setValue('wahaHost', data.wahaHost);
        setValue('wahaApiKey', data.wahaApiKey);
        setValue('evolutionHost', data.evolutionHost);
        setValue('evolutionApiKey', data.evolutionApiKey);
        setValue('quepasaUrl', data.quepasaUrl || '');
        setValue('quepasaLogin', data.quepasaLogin || '');
        setValue('quepasaPassword', data.quepasaPassword || '');
      }
    } catch (error) {
      console.error('Erro ao carregar configurações de integração:', error);
    }
  };

  const onIntegrationSubmit = async (data: SettingsFormData) => {
    try {
      const response = await authenticatedFetch('/api/settings', {
        method: 'PUT',
        body: JSON.stringify(data),
      });

      if (response.ok) {
        toast.success('Configurações de integração salvas com sucesso');
        setActiveModal(null);
        loadIntegrationSettings();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao salvar configurações');
      }
    } catch (error) {
      console.error('Erro ao salvar configurações de integração:', error);
      toast.error('Erro ao salvar configurações');
    }
  };

  // Logo handlers
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) return;

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);

      const response = await authenticatedUpload('/api/settings/logo', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        toast.success('Logo carregada com sucesso');
        loadGeneralSettings();
        setLogoFile(null);
        setLogoPreview('');
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao carregar logo');
      }
    } catch (error) {
      console.error('Erro ao carregar logo:', error);
      toast.error('Erro ao carregar logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = async () => {
    try {
      const response = await authenticatedFetch('/api/settings/logo', {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success('Logo removida com sucesso');
        loadGeneralSettings();
      } else {
        toast.error('Erro ao remover logo');
      }
    } catch (error) {
      console.error('Erro ao remover logo:', error);
      toast.error('Erro ao remover logo');
    }
  };

  // Favicon handlers
  const handleFaviconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFaviconFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setFaviconPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadFavicon = async () => {
    if (!faviconFile) return;

    setUploadingFavicon(true);
    try {
      const formData = new FormData();
      formData.append('favicon', faviconFile);

      const response = await authenticatedUpload('/api/settings/favicon', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        toast.success('Favicon carregado com sucesso');
        loadGeneralSettings();
        setFaviconFile(null);
        setFaviconPreview('');
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao carregar favicon');
      }
    } catch (error) {
      console.error('Erro ao carregar favicon:', error);
      toast.error('Erro ao carregar favicon');
    } finally {
      setUploadingFavicon(false);
    }
  };

  const removeFavicon = async () => {
    try {
      const response = await authenticatedFetch('/api/settings/favicon', {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success('Favicon removido com sucesso');
        loadGeneralSettings();
      } else {
        toast.error('Erro ao remover favicon');
      }
    } catch (error) {
      console.error('Erro ao remover favicon:', error);
      toast.error('Erro ao remover favicon');
    }
  };

  // Icon handlers
  const handleIconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIconFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setIconPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadIcon = async () => {
    if (!iconFile) return;

    setUploadingIcon(true);
    try {
      const formData = new FormData();
      formData.append('icon', iconFile);

      const response = await authenticatedUpload('/api/settings/icon', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        toast.success('Ícone carregado com sucesso');
        loadGeneralSettings();
        setIconFile(null);
        setIconPreview('');
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao carregar ícone');
      }
    } catch (error) {
      console.error('Erro ao carregar ícone:', error);
      toast.error('Erro ao carregar ícone');
    } finally {
      setUploadingIcon(false);
    }
  };

  const removeIcon = async () => {
    try {
      const response = await authenticatedFetch('/api/settings/icon', {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success('Ícone removido com sucesso');
        loadGeneralSettings();
      } else {
        toast.error('Erro ao remover ícone');
      }
    } catch (error) {
      console.error('Erro ao remover ícone:', error);
      toast.error('Erro ao remover ícone');
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');

      if (activeTab === 'dashboard') {
        // Carregar estatísticas do sistema para o dashboard
        const response = await fetch('/api/system/stats', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } else if (activeTab === 'tenants') {
        const response = await fetch('/api/tenants', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setTenants(data.tenants || []);
        }
      } else if (activeTab === 'users') {
        // Buscar TODOS os usuários de uma vez (sem paginação no backend)
        const params = new URLSearchParams({
          page: '1',
          pageSize: '10000', // Pega todos os usuários
        });

        const response = await fetch(`/api/users/global?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUsers(data.data?.users || []);
        }
      } else if (activeTab === 'backup') {
        const response = await fetch('/api/backup/stats', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setBackupStats(data.stats || null);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTenantStatus = async (tenant: Tenant) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/tenants/${tenant.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...tenant,
          active: !tenant.active
        })
      });

      if (response.ok) {
        toast.success(`Empresa ${!tenant.active ? 'ativada' : 'desativada'} com sucesso!`);
        loadData();
      } else {
        throw new Error('Erro ao alterar status da empresa');
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleCreateTenant = () => {
    navigate('/superadmin/empresas/novo');
  };

  const handleEditTenant = (tenant: Tenant) => {
    navigate(`/superadmin/empresas/${tenant.id}`);
  };

  const handleResetMessageCounter = async (tenantId: string) => {
    if (!confirm('Tem certeza que deseja resetar o contador de mensagens desta empresa?')) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/tenants/${tenantId}/quota/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Contador resetado com sucesso');
        loadData();
      } else {
        throw new Error('Erro ao resetar contador');
      }
    } catch (error) {
      toast.error('Erro ao resetar contador');
      console.error(error);
    }
  };

  const handleDeleteTenant = async (tenant: Tenant) => {
    if (!confirm(`Tem certeza que deseja excluir a empresa "${tenant.name}"? Esta ação é irreversível.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/tenants/${tenant.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Tenant excluído com sucesso!');
        loadData();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao excluir empresa');
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleSubmitTenant = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || (!editingTenant && (!formData.adminUser.nome || !formData.adminUser.email || !formData.adminUser.senha))) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const url = editingTenant ? `/api/tenants/${editingTenant.id}` : '/api/tenants';
      const method = editingTenant ? 'PUT' : 'POST';

      const body: any = {
        name: formData.name,
        quotas: formData.quotas,
        allowedProviders: formData.allowedProviders,
        timezone: formData.timezone || 'America/Sao_Paulo',
        // Converter expiresAt para ISO ou null se vazio
        expiresAt: formData.expiresAt ? new Date(formData.expiresAt).toISOString() : null
      };

      if (!editingTenant) {
        body.adminUser = formData.adminUser;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        toast.success(`Empresa ${editingTenant ? 'atualizada' : 'criada'} com sucesso!`);
        setIsModalOpen(false);
        loadData();
      } else {
        const error = await response.json();
        throw new Error(error.message || `Erro ao ${editingTenant ? 'atualizar' : 'criar'} empresa`);
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const getUsagePercentage = (current: number, max: number) => {
    if (max === 0) return 0; // 0 = ilimitado
    return Math.round((current / max) * 100);
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'text-red-600 bg-red-100';
    if (percentage >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };

  const formatQuotaValue = (current: number, max: number) => {
    if (max === 0) return `${current} / ∞`;
    return `${current} / ${max}`;
  };

  const handleToggleUserStatus = async (user: User) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ativo: !user.ativo
        })
      });

      if (response.ok) {
        toast.success(`Usuário ${!user.ativo ? 'ativado' : 'desativado'} com sucesso!`);
        loadData();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao alterar status do usuário');
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (!confirm(`Tem certeza que deseja excluir o usuário ${user.nome}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/users/${user.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Usuário excluído com sucesso!');
        loadData();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao excluir usuário');
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  return (
    <div className="flex-1 flex flex-col h-screen">
      <Header
        title="Super Admin"
        subtitle="Gerenciamento completo do sistema"
      />

      <div className="flex-1 overflow-auto bg-page">
        <PageLayout>
          <PageHeader
            title="Configurações Gerais"
            breadcrumbs={[
              { label: 'Super Admin', href: '/superadmin/dashboard' },
              { label: 'Configurações' }
            ]}
          />

      {/* Tabs */}
      {/* Empresas Tab */}
      {activeTab === 'tenants' && (
        <div className="space-y-8 w-full">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600 mb-4" />
              <p className="text-sm text-gray-600 dark:text-gray-400">Carregando empresas...</p>
            </div>
          ) : (
            (() => {
              const filteredTenants = tenants.filter((tenant) => {
                if (!tenantSearchQuery) return true;
                const query = tenantSearchQuery.toLowerCase();
                const matchesName = tenant.name.toLowerCase().includes(query);
                const matchesSlug = tenant.slug.toLowerCase().includes(query);
                const matchesEmail = tenant.users?.some(u => u.email?.toLowerCase().includes(query)) ?? false;
                return matchesName || matchesSlug || matchesEmail;
              });

              const totalTenants = tenants.length;
              const activeTenants = tenants.filter((t) => t.active).length;
              const inactiveTenants = tenants.filter((t) => !t.active).length;
              const expiringSoonTenants = tenants.filter((t) => {
                if (!t.expiresAt) return false;
                const expiresAt = new Date(t.expiresAt);
                const now = new Date();
                const daysUntilExpiration = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                return daysUntilExpiration > 0 && daysUntilExpiration <= 7;
              }).length;

              return (
                <>
                  {/* Card de Estatísticas */}
                  <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                    <div className="mb-6 flex items-center justify-between">
                      <h2 className="font-semibold text-gray-800 dark:text-white/90">Visão das empresas</h2>
                      <div className="flex items-center gap-2 flex-wrap justify-end">
                        <button
                          type="button"
                          onClick={loadData}
                          className="btn-atualizar"
                          title="Atualizar lista de empresas"
                        >
                          <i className="fas fa-arrows-rotate text-base shrink-0" aria-hidden />
                          Atualizar
                        </button>
                        <button
                          type="button"
                          onClick={handleCreateTenant}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-transparent text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors"
                        >
                          <i className="fas fa-plus text-xl shrink-0" aria-hidden />
                          Nova empresa
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 rounded-xl border border-gray-200 sm:grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-y-0 dark:divide-gray-800 dark:border-gray-800">
                      <div className="border-b p-5 sm:border-r lg:border-b-0">
                        <div className="mb-1.5 flex items-center gap-2">
                          <span className="text-blue-600 dark:text-blue-400 text-xl" aria-hidden>
                            <i className="fas fa-building" />
                          </span>
                          <p className="text-sm text-gray-400 dark:text-gray-500">Total de empresas</p>
                        </div>
                        <h3 className="text-3xl text-gray-800 dark:text-white/90">
                          {totalTenants}
                        </h3>
                      </div>

                      <div className="border-b p-5 sm:border-r lg:border-b-0">
                        <div className="mb-1.5 flex items-center gap-2">
                          <span className="text-emerald-600 dark:text-emerald-400 text-xl" aria-hidden>
                            <i className="fas fa-toggle-on" />
                          </span>
                          <p className="text-sm text-gray-400 dark:text-gray-500">Empresas ativas</p>
                        </div>
                        <h3 className="text-3xl text-gray-800 dark:text-white/90">
                          {activeTenants}
                        </h3>
                      </div>

                      <div className="border-b p-5 sm:border-r lg:border-b-0">
                        <div className="mb-1.5 flex items-center gap-2">
                          <span className="text-red-600 dark:text-red-400 text-xl" aria-hidden>
                            <i className="fas fa-toggle-off" />
                          </span>
                          <p className="text-sm text-gray-400 dark:text-gray-500">Empresas inativas</p>
                        </div>
                        <h3 className="text-3xl text-gray-800 dark:text-white/90">
                          {inactiveTenants}
                        </h3>
                      </div>

                      <div className="p-5">
                        <div className="mb-1.5 flex items-center gap-2">
                          <span className="text-amber-600 dark:text-amber-400 text-xl" aria-hidden>
                            <i className="fas fa-hourglass-half" />
                          </span>
                          <p className="text-sm text-gray-400 dark:text-gray-500">Expirando em até 7 dias</p>
                        </div>
                        <h3 className="text-3xl text-gray-800 dark:text-white/90">
                          {expiringSoonTenants}
                        </h3>
                      </div>
                    </div>
                  </div>

                  {/* Tabela de empresas */}
                  <div className="card-padrao overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-4">
                      <h2 className="font-semibold text-gray-800 dark:text-white/90">Empresas</h2>
                      <div className="flex items-center gap-3 flex-wrap">
                        {tenantSearchQuery && (
                          <button
                            type="button"
                            onClick={() => setTenantSearchQuery('')}
                            className="text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                          >
                            Limpar
                          </button>
                        )}
                        <div className="w-48 sm:w-64 relative">
                          <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden />
                          <input
                            type="text"
                            placeholder="Buscar..."
                            value={tenantSearchQuery}
                            onChange={(e) => setTenantSearchQuery(e.target.value)}
                            className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>
                    </div>

                    {filteredTenants.length === 0 ? (
                      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                        <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8h1m-1-4h1m4 4h1m-1-4h1" />
                        </svg>
                        <p className="mt-2 text-sm">
                          {tenantSearchQuery ? 'Nenhuma empresa encontrada com os filtros aplicados' : 'Nenhuma empresa encontrada'}
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800" role="table">
                          <thead className="bg-gray-50 dark:bg-white/[0.03]">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Empresa
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Slug
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Admin
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Vencimento
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Usuários
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Contatos
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Campanhas
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Conexões
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Ações
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-transparent divide-y divide-gray-200 dark:divide-gray-800">
                            {filteredTenants.map((tenant) => {
                              const adminUser = tenant.users?.find((u) => ['ADMIN', 'TENANT_ADMIN'].includes(u.role));
                              const adminEmail = adminUser?.email || 'N/A';

                              let expirationLabel = '—';
                              let expirationClass = 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
                              if (tenant.expiresAt) {
                                const expiresAt = new Date(tenant.expiresAt);
                                const now = new Date();
                                const daysUntilExpiration = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                                const isExpired = daysUntilExpiration <= 0;
                                const isExpiringSoon = daysUntilExpiration > 0 && daysUntilExpiration <= 7;
                                if (isExpired) {
                                  expirationLabel = 'Expirado';
                                  expirationClass = 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300';
                                } else if (isExpiringSoon) {
                                  expirationLabel = `${daysUntilExpiration}d restantes`;
                                  expirationClass = 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300';
                                } else {
                                  expirationLabel = expiresAt.toLocaleDateString('pt-BR');
                                  expirationClass = 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300';
                                }
                              }

                              return (
                                <tr key={tenant.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white/90">
                                    {tenant.name}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400 font-mono">
                                    {tenant.slug}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                    {adminEmail !== 'N/A' ? adminEmail : <span className="text-gray-400 dark:text-gray-500">—</span>}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <span
                                      className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${
                                        tenant.active
                                          ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                                          : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                                      }`}
                                    >
                                      {tenant.active ? 'Ativo' : 'Inativo'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${expirationClass}`}>
                                      {expirationLabel}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                    {tenant._count ? `${tenant._count.users}/${tenant.quota?.maxUsers ?? '∞'}` : '—'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                    {tenant._count ? `${tenant._count.contacts}/${tenant.quota?.maxContacts ?? '∞'}` : '—'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                    {tenant._count ? `${tenant._count.campaigns}/${tenant.quota?.maxCampaigns ?? '∞'}` : '—'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                    {tenant._count ? `${tenant._count.whatsappSessions}/${tenant.quota?.maxConnections ?? '∞'}` : '—'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <div className="flex items-center justify-end gap-2">
                                      <div ref={openActionsId === tenant.id ? actionsDropdownRef : undefined}>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (openActionsId === tenant.id) {
                                              setOpenActionsId(null);
                                              setDropdownPlacement(null);
                                            } else {
                                              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                              const openUp = rect.bottom + 220 > window.innerHeight;
                                              setDropdownPlacement({
                                                left: rect.right - 192,
                                                top: rect.bottom + 4,
                                                bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
                                                openUp,
                                              });
                                              setOpenActionsId(tenant.id);
                                            }
                                          }}
                                          className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                          title="Mais ações"
                                          aria-expanded={openActionsId === tenant.id}
                                        >
                                          <i className="fas fa-ellipsis-v text-sm" aria-hidden />
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              );
            })()
          )}

          {/* Dropdown de ações para empresas */}
          {openActionsId && dropdownPlacement && (() => {
            const openTenant = tenants.find(t => t.id === openActionsId);
            if (!openTenant) return null;
            const style: React.CSSProperties = {
              position: 'fixed',
              left: dropdownPlacement.left,
              width: 192,
              zIndex: 9999,
              ...(dropdownPlacement.openUp && dropdownPlacement.bottom !== undefined
                ? { bottom: dropdownPlacement.bottom, top: 'auto' }
                : { top: dropdownPlacement.top }),
            };
            return createPortal(
              <div
                ref={actionsMenuRef}
                className="py-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg"
                style={style}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenActionsId(null);
                    setDropdownPlacement(null);
                    handleEditTenant(openTenant);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <i className="fas fa-edit w-4" aria-hidden />
                  Editar
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenActionsId(null);
                    setDropdownPlacement(null);
                    handleToggleTenantStatus(openTenant);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <i className={`fas ${openTenant.active ? 'fa-toggle-off' : 'fa-toggle-on'} w-4`} aria-hidden />
                  {openTenant.active ? 'Desativar' : 'Ativar'}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenActionsId(null);
                    setDropdownPlacement(null);
                    handleDeleteTenant(openTenant);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-xl"
                >
                  <i className="fas fa-trash w-4" aria-hidden />
                  Excluir
                </button>
              </div>,
              document.body
            );
          })()}
        </div>
      )}

      {/* Users Tab - mesmo visual da aba Empresas */}
      {activeTab === 'users' && (
        <div className="space-y-8 w-full">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600 mb-4" />
              <p className="text-sm text-gray-600 dark:text-gray-400">Carregando usuários...</p>
            </div>
          ) : (
            (() => {
              const filteredUsers = users.filter((user) => {
                if (!userSearchQuery) return true;
                const query = userSearchQuery.toLowerCase();
                const matchesName = user.nome.toLowerCase().includes(query);
                const matchesEmail = user.email.toLowerCase().includes(query);
                const matchesTenant = user.tenant?.name.toLowerCase().includes(query) || user.tenant?.slug.toLowerCase().includes(query);
                return matchesName || matchesEmail || matchesTenant;
              });

              const totalUsers = users.length;
              const activeUsers = users.filter((u) => u.ativo).length;
              const inactiveUsers = users.filter((u) => !u.ativo).length;
              const superAdminCount = users.filter((u) => u.role === 'SUPERADMIN').length;

              const pageSize = 50;
              const totalPages = Math.ceil(filteredUsers.length / pageSize);
              const currentPage = totalPages > 0 ? Math.min(userPage, totalPages) : 1;
              const startIndex = (currentPage - 1) * pageSize;
              const endIndex = startIndex + pageSize;
              const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

              return (
                <>
                  {/* Card de estatísticas - mesmo padrão Empresas */}
                  <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                    <div className="mb-6 flex items-center justify-between">
                      <h2 className="font-semibold text-gray-800 dark:text-white/90">Visão dos usuários</h2>
                      <div className="flex items-center gap-2 flex-wrap justify-end">
                        <button
                          type="button"
                          onClick={loadData}
                          className="btn-atualizar"
                          title="Atualizar lista de usuários"
                        >
                          <i className="fas fa-arrows-rotate text-base shrink-0" aria-hidden />
                          Atualizar
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate('/superadmin/usuarios/novo')}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-transparent text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors"
                        >
                          <i className="fas fa-plus text-xl shrink-0" aria-hidden />
                          Novo usuário
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 rounded-xl border border-gray-200 sm:grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-y-0 dark:divide-gray-800 dark:border-gray-800">
                      <div className="border-b p-5 sm:border-r lg:border-b-0">
                        <div className="mb-1.5 flex items-center gap-2">
                          <span className="text-blue-600 dark:text-blue-400 text-xl" aria-hidden>
                            <i className="fas fa-users" />
                          </span>
                          <p className="text-sm text-gray-400 dark:text-gray-500">Total de usuários</p>
                        </div>
                        <h3 className="text-3xl text-gray-800 dark:text-white/90">{totalUsers}</h3>
                      </div>
                      <div className="border-b p-5 sm:border-r lg:border-b-0">
                        <div className="mb-1.5 flex items-center gap-2">
                          <span className="text-emerald-600 dark:text-emerald-400 text-xl" aria-hidden>
                            <i className="fas fa-toggle-on" />
                          </span>
                          <p className="text-sm text-gray-400 dark:text-gray-500">Usuários ativos</p>
                        </div>
                        <h3 className="text-3xl text-gray-800 dark:text-white/90">{activeUsers}</h3>
                      </div>
                      <div className="border-b p-5 sm:border-r lg:border-b-0">
                        <div className="mb-1.5 flex items-center gap-2">
                          <span className="text-red-600 dark:text-red-400 text-xl" aria-hidden>
                            <i className="fas fa-toggle-off" />
                          </span>
                          <p className="text-sm text-gray-400 dark:text-gray-500">Usuários inativos</p>
                        </div>
                        <h3 className="text-3xl text-gray-800 dark:text-white/90">{inactiveUsers}</h3>
                      </div>
                      <div className="p-5">
                        <div className="mb-1.5 flex items-center gap-2">
                          <span className="text-purple-600 dark:text-purple-400 text-xl" aria-hidden>
                            <i className="fas fa-user-shield" />
                          </span>
                          <p className="text-sm text-gray-400 dark:text-gray-500">Super Admin</p>
                        </div>
                        <h3 className="text-3xl text-gray-800 dark:text-white/90">{superAdminCount}</h3>
                      </div>
                    </div>
                  </div>

                  {/* Tabela de usuários - mesmo card padrão Empresas */}
                  <div className="card-padrao overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-4">
                      <h2 className="font-semibold text-gray-800 dark:text-white/90">Usuários</h2>
                      <div className="flex items-center gap-3 flex-wrap">
                        {userSearchQuery && (
                          <button
                            type="button"
                            onClick={() => setUserSearchQuery('')}
                            className="text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                          >
                            Limpar
                          </button>
                        )}
                        <div className="w-48 sm:w-64 relative">
                          <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden />
                          <input
                            type="text"
                            placeholder="Buscar..."
                            value={userSearchQuery}
                            onChange={(e) => setUserSearchQuery(e.target.value)}
                            className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>
                    </div>

                    {filteredUsers.length === 0 ? (
                      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                        <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <p className="mt-2 text-sm">
                          {userSearchQuery ? 'Nenhum usuário encontrado com os filtros aplicados' : 'Nenhum usuário encontrado'}
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" role="table">
                            <thead className="bg-gray-50 dark:bg-gray-800/50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Usuário</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Empresa</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Perfil</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Último login</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Criado em</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ações</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-white/[0.02] divide-y divide-gray-200 dark:divide-gray-700">
                              {paginatedUsers.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div>
                                      <div className="text-sm font-medium text-gray-900 dark:text-white/90">{user.nome}</div>
                                      <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {user.tenant ? (
                                      <div>
                                        <div className="text-sm text-gray-900 dark:text-white/90">{user.tenant.name}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">{user.tenant.slug}</div>
                                      </div>
                                    ) : (
                                      <span className="text-sm text-gray-400 dark:text-gray-500">Sistema</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${
                                      user.role === 'SUPERADMIN'
                                        ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                                        : user.role === 'ADMIN'
                                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                    }`}>
                                      {user.role === 'SUPERADMIN' ? 'Super Admin' : user.role === 'ADMIN' ? 'Admin' : 'Usuário'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${
                                      user.ativo
                                        ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                                        : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                                    }`}>
                                      {user.ativo ? 'Ativo' : 'Inativo'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                    {user.ultimoLogin ? new Date(user.ultimoLogin).toLocaleDateString('pt-BR') : 'Nunca'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                    {new Date(user.criadoEm).toLocaleDateString('pt-BR')}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <div className="flex justify-end">
                                      <div ref={openUserActionsId === user.id ? userActionsDropdownRef : undefined}>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (openUserActionsId === user.id) {
                                              setOpenUserActionsId(null);
                                              setUserDropdownPlacement(null);
                                            } else {
                                              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                              const openUp = rect.bottom + 180 > window.innerHeight;
                                              setUserDropdownPlacement({
                                                left: rect.right - 192,
                                                top: rect.bottom + 4,
                                                bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
                                                openUp,
                                              });
                                              setOpenUserActionsId(user.id);
                                            }
                                          }}
                                          className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                          title="Mais ações"
                                          aria-expanded={openUserActionsId === user.id}
                                        >
                                          <i className="fas fa-ellipsis-v text-sm" aria-hidden />
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Paginação */}
                        {totalPages > 1 && (
                          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Mostrando <span className="font-medium">{startIndex + 1}</span> até{' '}
                              <span className="font-medium">{Math.min(endIndex, filteredUsers.length)}</span> de{' '}
                              <span className="font-medium">{filteredUsers.length}</span> usuários
                            </p>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => setUserPage((p) => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-200"
                              >
                                Anterior
                              </button>
                              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum: number;
                                if (totalPages <= 5) pageNum = i + 1;
                                else if (currentPage <= 3) pageNum = i + 1;
                                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                else pageNum = currentPage - 2 + i;
                                return (
                                  <button
                                    key={pageNum}
                                    type="button"
                                    onClick={() => setUserPage(pageNum)}
                                    className={`px-3 py-1.5 text-sm rounded-lg ${
                                      currentPage === pageNum
                                        ? 'bg-emerald-600 text-white border border-transparent'
                                        : 'border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200'
                                    }`}
                                  >
                                    {pageNum}
                                  </button>
                                );
                              })}
                              <button
                                type="button"
                                onClick={() => setUserPage((p) => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-200"
                              >
                                Próxima
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Dropdown de ações do usuário (portal) */}
                        {openUserActionsId && userDropdownPlacement && (() => {
                          const user = users.find((u) => u.id === openUserActionsId);
                          if (!user) return null;
                          return createPortal(
                            <div
                              ref={userActionsMenuRef}
                              className="fixed z-[100] w-48 py-1 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg"
                              style={{
                                left: userDropdownPlacement.left,
                                ...(userDropdownPlacement.openUp
                                  ? { bottom: userDropdownPlacement.bottom }
                                  : { top: userDropdownPlacement.top }),
                              }}
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  setOpenUserActionsId(null);
                                  setUserDropdownPlacement(null);
                                  navigate(`/superadmin/usuarios/${user.id}`);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                Editar
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setOpenUserActionsId(null);
                                  setUserDropdownPlacement(null);
                                  handleToggleUserStatus(user);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                {user.ativo ? 'Desativar' : 'Ativar'}
                              </button>
                              {user.role !== 'SUPERADMIN' && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setOpenUserActionsId(null);
                                    setUserDropdownPlacement(null);
                                    handleDeleteUser(user);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                                >
                                  Excluir
                                </button>
                              )}
                            </div>,
                            document.body
                          );
                        })()}
                      </>
                    )}
                  </div>
                </>
              );
            })()
          )}
        </div>
      )}

      {/* Modal for Create/Edit Tenant */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingTenant ? 'Editar Empresa' : 'Criar Nova Empresa'}
              </h3>
            </div>

            <form onSubmit={handleSubmitTenant} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Coluna Esquerda - Dados da Empresa e Admin */}
                <div className="space-y-4">
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      Dados da Empresa
                    </h4>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Nome da Empresa *
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Nome da empresa"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">O identificador único será gerado automaticamente</p>
                    
                    {/* Data de Vencimento */}
                    <div className="mt-4">
                      <label htmlFor="expiresAt" className="block text-sm font-medium text-gray-700 mb-1">
                        Data de Vencimento
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="date"
                          id="expiresAt"
                          value={formData.expiresAt}
                          onChange={(e) => {
                            const newExpiresAt = e.target.value;
                            // Se mudou a data de expiração, atualizar billingCycleDay com o dia da nova data
                            // Se o dia for maior que 28, usar 28 (limite máximo para billingCycleDay)
                            let newBillingCycleDay = formData.quotas.billingCycleDay;
                            if (newExpiresAt) {
                              const date = new Date(newExpiresAt);
                              const day = date.getDate();
                              newBillingCycleDay = Math.min(28, day).toString();
                            }
                            setFormData({ 
                              ...formData, 
                              expiresAt: newExpiresAt,
                              quotas: {
                                ...formData.quotas,
                                billingCycleDay: newBillingCycleDay
                              }
                            });
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {formData.expiresAt && (
                          <button
                            type="button"
                            onClick={() => setFormData({ 
                              ...formData, 
                              expiresAt: '',
                              quotas: {
                                ...formData.quotas,
                                billingCycleDay: '1' // Resetar para default quando limpar expiração
                              }
                            })}
                            className="px-3 py-2 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                          >
                            Limpar
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.expiresAt 
                          ? `O tenant será desativado automaticamente em ${new Date(formData.expiresAt).toLocaleDateString('pt-BR')}`
                          : 'Deixe vazio para sem vencimento (acesso ilimitado)'
                        }
                      </p>
                    </div>
                  </div>

                  {!editingTenant && (
                    <div className="border-t border-gray-200 pt-4">
                      <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Usuário Administrador
                      </h4>

                      <div className="space-y-3">
                        <div>
                          <label htmlFor="adminNome" className="block text-sm font-medium text-gray-700 mb-1">
                            Nome do Admin *
                          </label>
                          <input
                            type="text"
                            id="adminNome"
                            value={formData.adminUser.nome}
                            onChange={(e) => setFormData({
                              ...formData,
                              adminUser: { ...formData.adminUser, nome: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Nome completo"
                            required={!editingTenant}
                          />
                        </div>

                        <div>
                          <label htmlFor="adminEmail" className="block text-sm font-medium text-gray-700 mb-1">
                            E-mail do Admin *
                          </label>
                          <input
                            type="email"
                            id="adminEmail"
                            value={formData.adminUser.email}
                            onChange={(e) => setFormData({
                              ...formData,
                              adminUser: { ...formData.adminUser, email: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="admin@exemplo.com"
                            required={!editingTenant}
                          />
                        </div>

                        <div>
                          <label htmlFor="adminSenha" className="block text-sm font-medium text-gray-700 mb-1">
                            Senha do Admin *
                          </label>
                          <input
                            type="password"
                            id="adminSenha"
                            value={formData.adminUser.senha}
                            onChange={(e) => setFormData({
                              ...formData,
                              adminUser: { ...formData.adminUser, senha: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Senha segura"
                            required={!editingTenant}
                            minLength={6}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Coluna Direita - Limites e Provedores */}
                <div className="space-y-4">
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      Limites e Quotas
                    </h4>
                    <p className="text-xs text-gray-500 mb-3">Use 0 para ilimitado</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="maxUsers" className="block text-sm font-medium text-gray-700 mb-1">
                          Máx. Usuários *
                        </label>
                        <input
                          type="number"
                          id="maxUsers"
                          min="0"
                          value={formData.quotas.maxUsers}
                          onChange={(e) => setFormData({
                            ...formData,
                            quotas: { ...formData.quotas, maxUsers: e.target.value }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="maxConnections" className="block text-sm font-medium text-gray-700 mb-1">
                          Máx. Conexões *
                        </label>
                        <input
                          type="number"
                          id="maxConnections"
                          min="0"
                          value={formData.quotas.maxConnections}
                          onChange={(e) => setFormData({
                            ...formData,
                            quotas: { ...formData.quotas, maxConnections: e.target.value }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="maxContacts" className="block text-sm font-medium text-gray-700 mb-1">
                          Máx. Contatos
                        </label>
                        <input
                          type="number"
                          id="maxContacts"
                          min="0"
                          value={formData.quotas.maxContacts}
                          onChange={(e) => setFormData({
                            ...formData,
                            quotas: { ...formData.quotas, maxContacts: e.target.value }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label htmlFor="maxCampaigns" className="block text-sm font-medium text-gray-700 mb-1">
                          Máx. Campanhas
                        </label>
                        <input
                          type="number"
                          id="maxCampaigns"
                          min="0"
                          value={formData.quotas.maxCampaigns}
                          onChange={(e) => setFormData({
                            ...formData,
                            quotas: { ...formData.quotas, maxCampaigns: e.target.value }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Limite de Mensagens */}
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      Limite de Mensagens
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="maxMessagesPerMonth" className="block text-sm font-medium text-gray-700 mb-1">
                          Máx. Mensagens/Mês
                        </label>
                        <input
                          type="number"
                          id="maxMessagesPerMonth"
                          min="0"
                          value={formData.quotas.maxMessagesPerMonth}
                          onChange={(e) => setFormData({
                            ...formData,
                            quotas: { ...formData.quotas, maxMessagesPerMonth: e.target.value }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="0 = ilimitado"
                        />
                      </div>
                      <div>
                        <label htmlFor="billingCycleDay" className="block text-sm font-medium text-gray-700 mb-1">
                          Dia do Reset (1-28)
                        </label>
                        <input
                          type="number"
                          id="billingCycleDay"
                          min="1"
                          max="28"
                          value={formData.quotas.billingCycleDay}
                          onChange={(e) => setFormData({
                            ...formData,
                            quotas: { ...formData.quotas, billingCycleDay: Math.min(28, Math.max(1, parseInt(e.target.value) || 1)).toString() }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    {editingTenant?.quota && (
                      <div className="mt-3 p-3 bg-white rounded-lg border border-blue-100">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-xs text-gray-600">Mensagens enviadas este mês:</p>
                            <p className="text-lg font-bold text-gray-900">
                              {formatQuotaValue(editingTenant.quota.messagesSentThisMonth || 0, editingTenant.quota.maxMessagesPerMonth || 0)}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleResetMessageCounter(editingTenant.id)}
                            className="px-3 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 text-sm font-medium"
                          >
                            Resetar Contador
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-md font-medium text-gray-900 mb-2 flex items-center gap-2">
                      <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      Provedores WhatsApp
                    </h4>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs text-gray-500">Selecione quais provedores de API a empresa poderá utilizar</p>
                      <button
                        type="button"
                        onClick={() => {
                          const allProviders = ['WAHA', 'EVOLUTION', 'QUEPASA'];
                          const allSelected = allProviders.every(p => formData.allowedProviders.includes(p));
                          setFormData({
                            ...formData,
                            allowedProviders: allSelected ? [] : allProviders
                          });
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {formData.allowedProviders.length === 3 ? 'Desmarcar Todos' : 'Selecionar Todos'}
                      </button>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer border border-gray-200">
                        <input
                          type="checkbox"
                          checked={formData.allowedProviders.includes('WAHA')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, allowedProviders: [...formData.allowedProviders, 'WAHA'] });
                            } else {
                              setFormData({ ...formData, allowedProviders: formData.allowedProviders.filter(p => p !== 'WAHA') });
                            }
                          }}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <img src="/iconewaha.png" alt="WAHA" className="w-6 h-6 object-contain" />
                        <span className="text-sm font-medium text-gray-700">WAHA</span>
                      </label>
                      <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer border border-gray-200">
                        <input
                          type="checkbox"
                          checked={formData.allowedProviders.includes('EVOLUTION')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, allowedProviders: [...formData.allowedProviders, 'EVOLUTION'] });
                            } else {
                              setFormData({ ...formData, allowedProviders: formData.allowedProviders.filter(p => p !== 'EVOLUTION') });
                            }
                          }}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <img src="/iconeevolutionapi.png" alt="Evolution API" className="w-6 h-6 object-contain" />
                        <span className="text-sm font-medium text-gray-700">Evolution API</span>
                      </label>
                      <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer border border-gray-200">
                        <input
                          type="checkbox"
                          checked={formData.allowedProviders.includes('QUEPASA')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, allowedProviders: [...formData.allowedProviders, 'QUEPASA'] });
                            } else {
                              setFormData({ ...formData, allowedProviders: formData.allowedProviders.filter(p => p !== 'QUEPASA') });
                            }
                          }}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <img src="/iconequepasa.png" alt="Quepasa" className="w-6 h-6 object-contain" />
                        <span className="text-sm font-medium text-gray-700">Quepasa</span>
                      </label>
                    </div>
                    {formData.allowedProviders.length === 0 && (
                      <p className="text-xs text-red-500 mt-2">Selecione pelo menos um provedor</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                >
                  {editingTenant ? 'Atualizar' : 'Criar'} Empresa
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Integrations Tab */}
      {activeTab === 'integrations' && (
        <div className="card-padrao overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-sm font-semibold text-gray-800 dark:text-white">
              Integrações de WhatsApp
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Configure provedores de API de WhatsApp para envio e recebimento de mensagens
            </p>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* WAHA Card */}
              <button
                type="button"
                onClick={() => setActiveModal('waha')}
                className="text-left rounded-xl border border-gray-200 dark:border-gray-600 p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors flex flex-col justify-between gap-4"
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center shrink-0">
                      <img
                        src="/assets/logos/waha.png"
                        alt="WAHA Logo"
                        className="w-8 h-5 object-contain"
                      />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white">WAHA</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">WhatsApp HTTP API</p>
                    </div>
                  </div>
                  <span
                    className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-lg ${
                      integrationSettings?.wahaHost && integrationSettings?.wahaApiKey
                        ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {integrationSettings?.wahaHost && integrationSettings?.wahaApiKey ? 'Configurado' : 'Não configurado'}
                  </span>
                </div>
                <div className="flex justify-end">
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">
                    Configurar
                  </span>
                </div>
              </button>

              {/* Evolution API Card */}
              <button
                type="button"
                onClick={() => setActiveModal('evolution')}
                className="text-left rounded-xl border border-gray-200 dark:border-gray-600 p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors flex flex-col justify-between gap-4"
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
                      <img
                        src="/assets/logos/evolutionapi.png"
                        alt="Evolution API Logo"
                        className="w-8 h-5 object-contain"
                      />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white">Evolution API</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">WhatsApp API Evolution</p>
                    </div>
                  </div>
                  <span
                    className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-lg ${
                      integrationSettings?.evolutionHost && integrationSettings?.evolutionApiKey
                        ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {integrationSettings?.evolutionHost && integrationSettings?.evolutionApiKey ? 'Configurado' : 'Não configurado'}
                  </span>
                </div>
                <div className="flex justify-end">
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">
                    Configurar
                  </span>
                </div>
              </button>

              {/* Quepasa Card */}
              <button
                type="button"
                onClick={() => setActiveModal('quepasa')}
                className="text-left rounded-xl border border-gray-200 dark:border-gray-600 p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors flex flex-col justify-between gap-4"
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-violet-50 dark:bg-violet-900/30 flex items-center justify-center shrink-0">
                      <img
                        src="/logoquepasa.png?v=2"
                        alt="Quepasa Logo"
                        className="w-8 h-5 object-contain"
                      />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white">Quepasa</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">WhatsApp API Quepasa</p>
                    </div>
                  </div>
                  <span
                    className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-lg ${
                      integrationSettings?.quepasaUrl && integrationSettings?.quepasaLogin && integrationSettings?.quepasaPassword
                        ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {integrationSettings?.quepasaUrl && integrationSettings?.quepasaLogin && integrationSettings?.quepasaPassword ? 'Configurado' : 'Não configurado'}
                  </span>
                </div>
                <div className="flex justify-end">
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">
                    Configurar
                  </span>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backup Tab */}
      {activeTab === 'backup' && (
        <SystemBackup />
      )}


      {/* Geral + Aparência (mesma página) */}
      {activeTab === 'general' && (
        <div className="card-padrao">
          <form onSubmit={handleGeneralSubmit(onGeneralSubmit)}>
            {/* Informações da Empresa */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Informações da Empresa
              </h2>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nome da Empresa *
                  </label>
                  <input
                    id="companyName"
                    type="text"
                    {...registerGeneral('companyName')}
                    placeholder="Nome da sua empresa"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-white/[0.05] dark:text-white"
                  />
                  {generalErrors.companyName && (
                    <p className="text-red-500 text-sm mt-1">
                      {generalErrors.companyName.message}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="pageTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Título da Página *
                  </label>
                  <input
                    id="pageTitle"
                    type="text"
                    {...registerGeneral('pageTitle')}
                    placeholder="Ex: AutoWhats - Gestão de Contatos"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-white/[0.05] dark:text-white"
                  />
                  {generalErrors.pageTitle && (
                    <p className="text-red-500 text-sm mt-1">
                      {generalErrors.pageTitle.message}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Título que aparecerá na aba do navegador
                  </p>
                </div>
              </div>
            </div>

          {/* Aparência (logo, ícone, favicon) */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Aparência
            </h2>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Logo da Empresa */}
              <div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-white/90 mb-4">
                  Logo da Empresa
                </h3>

                {/* Logo Atual */}
                {generalSettings?.logoUrl && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Logo Atual:</p>
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 flex items-center justify-center overflow-hidden shadow-sm">
                        <img
                          src={generalSettings.logoUrl}
                          alt="Logo atual"
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-medium border border-transparent text-white bg-red-600 hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                      >
                        <i className="fas fa-trash-alt text-sm shrink-0" aria-hidden />
                        Remover
                      </button>
                    </div>
                  </div>
                )}

                {/* Upload Nova Logo */}
                <div className="space-y-4">
                  <div>
                    <label htmlFor="logo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {generalSettings?.logoUrl ? 'Alterar Logo' : 'Nova Logo'}
                    </label>
                    <input
                      id="logo"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="block w-full text-sm text-gray-900 dark:text-gray-100 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 border border-dashed border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-900/40 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Formatos aceitos: JPEG, PNG, GIF, WebP (max 5MB). Use uma imagem com fundo transparente para melhor resultado.
                    </p>
                  </div>

                  {/* Preview */}
                  {logoPreview && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Preview:</p>
                      <div className="flex items-center space-x-4">
                        <img
                          src={logoPreview}
                          alt="Preview"
                          className="w-20 h-20 object-contain border border-gray-200 rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={uploadLogo}
                          disabled={uploadingLogo}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-transparent text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {uploadingLogo ? (
                            <>
                              <i className="fas fa-spinner fa-spin text-base shrink-0" aria-hidden />
                              Carregando...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-upload text-base shrink-0" aria-hidden />
                              Carregar Logo
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Ícone e Favicon na coluna direita */}
              <div className="space-y-6">
                {/* Ícone Geral */}
                <div className="space-y-3">
                  <h3 className="text-base font-semibold text-gray-800 dark:text-white/90 mb-4">
                    Ícone Geral
                  </h3>

                  {/* Ícone Atual */}
                  {generalSettings?.iconUrl && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ícone Atual:</p>
                      <div className="flex items-center space-x-4">
                        <img
                          src={generalSettings.iconUrl}
                          alt="Ícone atual"
                          className="w-12 h-12 object-contain border border-gray-200 dark:border-gray-700 rounded"
                        />
                        <button
                          type="button"
                          onClick={removeIcon}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border border-transparent text-white bg-red-600 hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                        >
                          <i className="fas fa-trash-alt text-sm shrink-0" aria-hidden />
                          Remover
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Upload Novo Ícone */}
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="icon" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {generalSettings?.iconUrl ? 'Alterar Ícone' : 'Novo Ícone'}
                      </label>
                      <input
                        id="icon"
                        type="file"
                        accept="image/*"
                        onChange={handleIconChange}
                        className="block w-full text-sm text-gray-900 dark:text-gray-100 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 border border-dashed border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-900/40 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Ícone usado na página de login e no menu interno. Recomendado: PNG, 64x64px ou similar (max 5MB).
                      </p>
                    </div>

                    {/* Preview */}
                    {iconPreview && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Preview:</p>
                        <div className="flex items-center space-x-4">
                          <img
                            src={iconPreview}
                            alt="Preview"
                            className="w-12 h-12 object-contain border border-gray-200 rounded"
                          />
                          <button
                            type="button"
                            onClick={uploadIcon}
                            disabled={uploadingIcon}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-transparent text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {uploadingIcon ? (
                              <>
                                <i className="fas fa-spinner fa-spin text-base shrink-0" aria-hidden />
                                Carregando...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-upload text-base shrink-0" aria-hidden />
                                Carregar Ícone
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Favicon da Plataforma */}
                <div className="space-y-3">
                  <h3 className="text-base font-semibold text-gray-800 dark:text-white/90 mb-4">
                    Favicon da Plataforma
                  </h3>

                  {/* Favicon Atual */}
                  {generalSettings?.faviconUrl && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Favicon Atual:</p>
                      <div className="flex items-center space-x-4">
                        <img
                          src={generalSettings.faviconUrl}
                          alt="Favicon atual"
                          className="w-8 h-8 object-contain border border-gray-200 dark:border-gray-700 rounded"
                        />
                        <button
                          type="button"
                          onClick={removeFavicon}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border border-transparent text-white bg-red-600 hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                        >
                          <i className="fas fa-trash-alt text-sm shrink-0" aria-hidden />
                          Remover
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Upload Novo Favicon */}
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="favicon" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {generalSettings?.faviconUrl ? 'Alterar Favicon' : 'Novo Favicon'}
                      </label>
                      <input
                        id="favicon"
                        type="file"
                        accept="image/*"
                        onChange={handleFaviconChange}
                        className="block w-full text-sm text-gray-900 dark:text-gray-100 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 border border-dashed border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-900/40 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Ícone exibido na aba do navegador. Formatos aceitos: PNG, ICO (recomendado 32x32px ou 16x16px).
                      </p>
                    </div>

                    {/* Preview */}
                    {faviconPreview && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Preview:</p>
                        <div className="flex items-center space-x-4">
                          <img
                            src={faviconPreview}
                            alt="Preview"
                            className="w-8 h-8 object-contain border border-gray-200 rounded"
                          />
                          <button
                            type="button"
                            onClick={uploadFavicon}
                            disabled={uploadingFavicon}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-transparent text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {uploadingFavicon ? (
                              <>
                                <i className="fas fa-spinner fa-spin text-base shrink-0" aria-hidden />
                                Carregando...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-upload text-base shrink-0" aria-hidden />
                                Carregar Favicon
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Botão Salvar no final da página */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
            <button
              type="submit"
              disabled={isGeneralSubmitting}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-transparent text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isGeneralSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin text-base shrink-0" aria-hidden />
                  Salvando...
                </>
              ) : (
                <>
                  <i className="fas fa-check text-base shrink-0" aria-hidden />
                  Salvar Configurações
                </>
              )}
            </button>
          </div>
        </form>
        </div>
      )}

      {/* Modal WAHA */}
      {activeModal === 'waha' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Configurar WAHA</h3>
              <button
                type="button"
                onClick={() => setActiveModal(null)}
                className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Fechar"
              >
                <i className="fas fa-times" aria-hidden />
              </button>
            </div>

            <form onSubmit={handleFormSubmit(onIntegrationSubmit)} className="space-y-4">
              <div>
                <label htmlFor="wahaHost" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Host WAHA *
                </label>
                <input
                  id="wahaHost"
                  type="url"
                  {...register('wahaHost')}
                  placeholder="https://waha.exemplo.com.br"
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
                {errors.wahaHost && (
                  <p className="text-red-500 text-sm mt-1">{errors.wahaHost.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="wahaApiKey" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  API Key WAHA *
                </label>
                <input
                  id="wahaApiKey"
                  type="password"
                  {...register('wahaApiKey')}
                  placeholder="sua-api-key-aqui"
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
                {errors.wahaApiKey && (
                  <p className="text-red-500 text-sm mt-1">{errors.wahaApiKey.message}</p>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setActiveModal(null)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2.5 text-sm font-medium bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                >
                  {isSubmitting ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Evolution API */}
      {activeModal === 'evolution' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Configurar Evolution API</h3>
              <button
                type="button"
                onClick={() => setActiveModal(null)}
                className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Fechar"
              >
                <i className="fas fa-times" aria-hidden />
              </button>
            </div>

            <form onSubmit={handleFormSubmit(onIntegrationSubmit)} className="space-y-4">
              <div>
                <label htmlFor="evolutionHost" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Host Evolution *
                </label>
                <input
                  id="evolutionHost"
                  type="url"
                  {...register('evolutionHost')}
                  placeholder="https://evolution.exemplo.com.br"
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
                {errors.evolutionHost && (
                  <p className="text-red-500 text-sm mt-1">{errors.evolutionHost.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="evolutionApiKey" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  API Key Evolution *
                </label>
                <input
                  id="evolutionApiKey"
                  type="password"
                  {...register('evolutionApiKey')}
                  placeholder="sua-api-key-evolution-aqui"
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
                {errors.evolutionApiKey && (
                  <p className="text-red-500 text-sm mt-1">{errors.evolutionApiKey.message}</p>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setActiveModal(null)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2.5 text-sm font-medium bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                >
                  {isSubmitting ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Quepasa */}
      {activeModal === 'quepasa' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <img src="/logoquepasa.png?v=2" alt="Quepasa" className="w-6 h-6 object-contain" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Configurar Quepasa</h3>
              </div>
              <button
                type="button"
                onClick={() => setActiveModal(null)}
                className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Fechar"
              >
                <i className="fas fa-times" aria-hidden />
              </button>
            </div>

            <form onSubmit={handleFormSubmit(onIntegrationSubmit)} className="space-y-4">
              <div>
                <label htmlFor="quepasaUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  URL do Quepasa *
                </label>
                <input
                  id="quepasaUrl"
                  type="url"
                  {...register('quepasaUrl')}
                  placeholder="https://quepasa.exemplo.com.br"
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
                {errors.quepasaUrl && (
                  <p className="text-red-500 text-sm mt-1">{errors.quepasaUrl.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="quepasaLogin" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Login *
                </label>
                <input
                  id="quepasaLogin"
                  type="text"
                  {...register('quepasaLogin')}
                  placeholder="admin"
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
                {errors.quepasaLogin && (
                  <p className="text-red-500 text-sm mt-1">{errors.quepasaLogin.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="quepasaPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Senha *
                </label>
                <input
                  id="quepasaPassword"
                  type="password"
                  {...register('quepasaPassword')}
                  placeholder="••••••••••••••••"
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
                {errors.quepasaPassword && (
                  <p className="text-red-500 text-sm mt-1">{errors.quepasaPassword.message}</p>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setActiveModal(null)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2.5 text-sm font-medium bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                >
                  {isSubmitting ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
        </PageLayout>
      </div>
    </div>
  );
}